import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildDemographicsFollowersFetchKey,
  formatDemographicsReportDate,
} from "../../lib/utils/demographicsFollowers"
import { normalizePulseApiPayload } from "../../lib/utils/pulseApiRows"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

/**
 * GET /api/v1/report/page/clients/{clientId}/demographics/followers?startDate=&endDate=
 */
export const fetchClientDemographicsFollowers = createAsyncThunk(
  "clientDemographicsFollowers/fetch",
  async ({ clientId, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const start = formatDemographicsReportDate(startDate)
      const end = formatDemographicsReportDate(endDate)
      const query = new URLSearchParams({ startDate: start, endDate: end }).toString()
      const data = await get(
        `report/page/clients/${clientId}/demographics/followers?${query}`,
      )

      return {
        rows: normalizePulseApiPayload(data),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildDemographicsFollowersFetchKey(clientId, start, end),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch demographics followers")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { clientId, startDate, endDate } = arg || {}
      const start = formatDemographicsReportDate(startDate)
      const end = formatDemographicsReportDate(endDate)
      const key = buildDemographicsFollowersFetchKey(clientId, start, end)
      return !skipIfFetchKeyLoaded(getState().clientDemographicsFollowers, key)
    },
  },
)

const initialState = {
  rows: [],
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginFetch(state, action) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatDemographicsReportDate(startDate)
  const end = formatDemographicsReportDate(endDate)
  state.status = "loading"
  state.error = null
  state.requestedFetchKey = buildDemographicsFollowersFetchKey(clientId, start, end)
  state.fetchedFetchKey = null
  state.rows = []
}

function isStaleFetch(action, state) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatDemographicsReportDate(startDate)
  const end = formatDemographicsReportDate(endDate)
  const key = buildDemographicsFollowersFetchKey(clientId, start, end)
  return key !== String(state.requestedFetchKey ?? "")
}

const clientDemographicsFollowersSlice = createSlice({
  name: "clientDemographicsFollowers",
  initialState,
  reducers: {
    clearClientDemographicsFollowers: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientDemographicsFollowers.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientDemographicsFollowers.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.rows = action.payload?.rows ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientDemographicsFollowers.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch demographics followers"
        state.rows = []
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientDemographicsFollowers } = clientDemographicsFollowersSlice.actions
export default clientDemographicsFollowersSlice.reducer
